import axios from 'axios';
import apiClient from './apiClient';

/**
 * The sales demo's session API.
 *
 * These endpoints only exist on the sales-demo stack; everywhere else the backend router 404s.
 * Nothing here should be called unless isDemoMode() is true.
 */

export type DemoPersonaRole = 'student' | 'teacher' | 'parent' | 'admin';

export interface DemoPersona {
  role: DemoPersonaRole;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  token: string;
}

export interface DemoSession {
  sandboxId: number;
  tenantId: number;
  expiresAt: string | null;
  personas: DemoPersona[];
}

/** Whether this build is the sales demo. Kept in one place so the check cannot drift. */
export const isDemoMode = (): boolean => process.env.REACT_APP_DEMO_MODE === 'true';

export const startDemoSession = async (): Promise<DemoSession> => {
  const response = await apiClient.post<DemoSession>('/api/demo/session');
  return response.data;
};

/**
 * Reload an existing session after a page refresh.
 *
 * Returns null when the sandbox has expired or been recycled, which is an expected outcome rather
 * than an error — the caller starts a fresh session.
 */
export const resumeDemoSession = async (sandboxId: number): Promise<DemoSession | null> => {
  try {
    const response = await apiClient.get<DemoSession>(`/api/demo/session/${sandboxId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const resetDemoSession = async (sandboxId: number): Promise<DemoSession> => {
  const response = await apiClient.post<DemoSession>(`/api/demo/session/${sandboxId}/reset`);
  return response.data;
};

/**
 * Complete a checkout without PayPal.
 *
 * The pricing, discount code, payment record and enrolment are all real — only the PayPal round
 * trip is skipped, so the confirmation and receipt screens the visitor lands on are the genuine
 * ones. Returns the same shape as capturePayPalPayment so callers are unchanged.
 */
export const completeDemoCheckout = async (
  sessionIds: number[],
  discountCode?: string
): Promise<{ message: string; payment: unknown }> => {
  const response = await apiClient.post('/api/demo/checkout', { sessionIds, discountCode });
  return response.data;
};
